/**
 * Carregamento do índice e revalidação — o mesmo ciclo da fumaça.
 *
 * Só baixa quando uma das subcamadas é ligada. Depois, revalida:
 * - a cada ciclo da atualização automática (gancho do loop);
 * - quando a janela do loop anda (chegou imagem nova do satélite).
 * O índice tem poucos KB comprimido e a máquina responde 304 quando nada mudou.
 */
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { BASE_RASTREIO, carregarIndice, type IndiceRastreio } from './api';
import { emUso, useTrackingStore } from './store';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;
/** A camada registra aqui o "tente de novo os que falharam". */
let tentarDeNovo: (() => void) | null = null;

export function registrarNovaTentativa(fn: (() => void) | null): void {
  tentarDeNovo = fn;
}

function mesmoIndice(a: IndiceRastreio | null, b: IndiceRastreio): boolean {
  if (!a || a.quadros.length !== b.quadros.length) return false;
  if (a.antecedencias.join() !== b.antecedencias.join() || a.limiares.join() !== b.limiares.join()) return false;
  // `prev` muda depois do quadro publicado: a previsão sai no fim da rodada.
  return a.quadros.every((q, i) => q.c === b.quadros[i].c && q.n === b.quadros[i].n && q.prev === b.quadros[i].prev);
}

export async function carregarRastreio(force = false): Promise<void> {
  if (!BASE_RASTREIO) return;
  const { situacao } = useTrackingStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  // A revalidação é silenciosa: "Baixando…" piscando a cada ciclo é ruído.
  if (!jaCarregou) useTrackingStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const indice = await carregarIndice(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Mesmos quadros: não trocar poupa replanejar a camada.
    const atual = useTrackingStore.getState().indice;
    useTrackingStore.setState({
      indice: mesmoIndice(atual, indice) ? { ...atual!, gerado: indice.gerado } : indice,
      situacao: { estado: 'ready', erro: null },
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useTrackingStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com alguma subcamada em uso. */
export function refreshRastreio(): void {
  if (!emUso()) return;
  tentarDeNovo?.();
  const nuncaCarregou = !jaCarregou && useTrackingStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarRastreio(true);
}

export function iniciarSyncRastreio(): () => void {
  if (emUso()) void carregarRastreio();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshRastreio();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useTrackingStore.subscribe((a, b) => {
      if (!emUso(a) || emUso(b)) return;
      // Religar depois de um tempo: o índice guardado pode ser de horas atrás.
      if (!jaCarregou) void carregarRastreio();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarRastreio(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshRastreio),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Instante do quadro mais novo publicado, ou null. */
export function ultimoQuadro(indice: IndiceRastreio | null): number | null {
  const q = indice?.quadros;
  return q?.length ? q[q.length - 1].t : null;
}

/** Só para os testes. */
export function resetTrackingSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
